import { css } from '@emotion/react'

import { colors, gridBreakpoints } from '@blissbook/ui/branding'
import {
  BackgroundShapeGrid,
  Container,
  DesktopBrowser,
  ForwardLink,
  Link,
  MobileBrowser,
} from '@blissbook/ui/lib'
import {
  Card,
  MarketingLayout,
  MaxWidthContainer,
  Section,
  Typography,
} from '../components'
import { CallToActionSection, SolutionSection } from './sections'

import config from '@blissbook/ui-config'
const { assetsUrl = '' } = config
const imgFolder =
  assetsUrl + '/images/marketing/resources/enterprise-policy-management/'
const imgFolderAlt =
  assetsUrl + '/images/marketing/resources/hr-policy-content-services/'

const assets = {
  policyLibrary: imgFolder + 'policy-library.png',
  brain: imgFolder + 'brain.png',
  ffrown: imgFolder + 'ffrown.svg',
  thumbsUp: imgFolder + 'thumbs-up.svg',
  reviewRequest: imgFolder + 'review-feedback-request.png',
  logos: imgFolder + 'logos.png',
  queryBuilder: imgFolder + 'query-builder.png',
  dashboard: imgFolder + 'dashboard.png',
  wordToBlissbook: imgFolder + 'word-to-blissbook.png',
  versionHistoryIllustration: imgFolder + 'version-history-illustration.png',
  eSignature: assetsUrl + '/images/marketing/home/' + 'Track-box.svg',
  reporting: imgFolder + 'reporting-basic.png',
  annotation: imgFolder + 'annotation.png',
  workdayLogo: imgFolder + 'workday.png',
  ukgLogo: imgFolder + 'ukg.png',
  activeDirLogo: imgFolder + 'active-directory.png',
  dayforceLogo:
    'https://merge-api-production.s3.amazonaws.com/media/Dayforce.png',
  oktaLogo: imgFolder + 'okta.png',
  samlLogo: imgFolder + 'saml.png',
  adpLogo: imgFolder + 'adp.png',
  bambooLogo: imgFolder + 'bamboo-hr.png',
  gWorkspaceLogo: imgFolder + 'google-workspace.png',
  chapterVisibility: imgFolder + 'chapter-visibility.png',
  manager: imgFolder + 'manager-and-staff.svg',
  lawChangeAlerts: imgFolderAlt + 'law-change-alerts-illustration.png',
}

export default () => (
  <MarketingLayout
    title='Policy Management Software, the Blissbook way'
    description='Policy Management Software: Manage policies across their full lifecycle and employees’ relationships with their handbooks, policies, procedures, SOPs, and more.'
    className='enterprise-policy-management'
  >
    <SolutionSection
      color='red-500'
      image='Building.svg'
      imageDesktopCss={css(`
          top: 70px;
          right: -250px;
          height: 400px;

          @media (min-width: ${gridBreakpoints.xl}px) {
            right: -220px;
          }
        `)}
      imageMobileCss={css(`
          transform: scaleX(-1);
        `)}
      greeting='Do your Policies spark joy?'
      title='Blissfully Simple Policy Management.'
      description='Manage the entire lifecycle of your policies, procedures, and SOPs with confidence, then easily distribute to your workforce, no matter how big.'
    />

    <Section>
      <Container className='tw-py-14 tw-mb-14'>
        <MaxWidthContainer className='tw-mx-auto' xs={960}>
          <div className='tw-flex tw-flex-col tw-items-center lg:tw-flex-row-reverse tw-gap-8'>
            <div className='tw-flex-1 tw-flex tw-items-center tw-justify-center'>
              <img
                className='tw-p-1 box-shadow'
                alt='a screenshot from Blissbook of a polivy review awaiting feedback'
                src={assets.reviewRequest}
                style={{
                  height: '255px',
                  width: '400px',
                }}
              />
            </div>
            <div className='tw-flex-1 tw-flex tw-flex-col tw-justify-center'>
              <Typography variant='h2'>
                Teamwork (+ Policy Workflows) Makes the Dream Work
              </Typography>
              <Typography variant='p2'>
                Collaborate in real-time with comments, suggested edits, and
                review/approval workflows. Per-policy sharing and personalized
                workspaces keep your team aligned with what needs to happen
                next.
              </Typography>
            </div>
          </div>
        </MaxWidthContainer>
      </Container>
    </Section>

    <Section style={{ backgroundColor: colors['blurple-50'] }}>
      <Container className='py-sm text-center'>
        <MaxWidthContainer className='tw-mx-auto' xs={960}>
          <img
            alt='a Brain'
            src={assets.brain}
            className='tw-mx-auto tw-mb-8'
            style={{
              height: '96px',
              width: '94px',
            }}
          />
          <Typography variant='h2'>
            Employee Experience &amp; Engagement
          </Typography>
          <Typography variant='p1'>
            Change how your employees think about their employment relationship:
          </Typography>
          <div className='row lg:tw-mx-18 tw-items-center'>
            <div className='col-lg-6 tw-my-8'>
              <Card
                className='text-left tw-mx-auto lg:tw-mb-0 tw-mb-14'
                color='sunshine-500'
                style={{ maxWidth: '370px', overflow: 'visible' }}
                title='From:'
                titleCss={{ textAlign: 'left' }}
                shape={
                  <img
                    alt='face frown emoji'
                    src={assets.ffrown}
                    className='tw-mx-auto tw-mb-8'
                    style={{
                      position: 'absolute',
                      right: 20,
                      top: -13,
                      height: '46px',
                      width: '46px',
                    }}
                  />
                }
              >
                <Typography variant='p2'>
                  I’m entering a legal transaction between two adversarial
                  parties.
                </Typography>
              </Card>
            </div>
            <div className='col-lg-6 tw-my-8'>
              <Card
                className='text-left tw-mx-auto'
                color='aqua-500'
                style={{ maxWidth: '370px', overflow: 'visible' }}
                title='To:'
                titleCss={{ textAlign: 'left' }}
                shape={
                  <img
                    alt='thumbs up emoji'
                    src={assets.thumbsUp}
                    className='tw-mx-auto tw-mb-8'
                    style={{
                      position: 'absolute',
                      right: 20,
                      top: -25,
                      height: '55px',
                      width: '47px',
                    }}
                  />
                }
              >
                <Typography variant='p2'>
                  I’m joining a team on a mission with some rules for how we
                  work together successfully.
                </Typography>
              </Card>
            </div>
          </div>
        </MaxWidthContainer>
      </Container>
    </Section>

    <Section className='tw-py-14'>
      <Container className='py-sm'>
        <MaxWidthContainer className='tw-mx-auto' xs={960}>
          <div className='row tw-flex-row-reverse tw-items-center'>
            <div className='col-lg-6'>
              <DesktopBrowser
                className='box-shadow tw-mx-auto tw-mb-18 lg:tw-mb-0'
                css={css(`
                    width: 400px;
                    max-width: 100%;
                    @media (min-width: ${gridBreakpoints.lg}px) {
                      width: 820px;
                      max-width: 820px;
                    }
                  `)}
              >
                <img
                  src={assets.policyLibrary}
                  style={{ width: '100%' }}
                  alt='an example of a dashboard with some fake data'
                />
              </DesktopBrowser>
            </div>
            <div className='col-lg-6'>
              <Typography variant='h4' style={{ color: colors['red-500'] }}>
                Organization FTW
              </Typography>
              <Typography variant='h2'>
                Control the Chaos with Blissbook's Policy Library
              </Typography>
              <Typography variant='p2'>
                Every policy at your fingertips. Organize, track, and manage
                policies in one place, so nothing gets lost, stalled, or left
                behind in your review and approval process.
              </Typography>
              <Typography variant='p2'>
                Custom properties, filters, grouping, and saved views turn your
                policy library into personalized workspaces for every member of
                your team.
              </Typography>
            </div>
          </div>
        </MaxWidthContainer>
      </Container>
    </Section>

    <Section className='tw-mt-14 lg:tw-mt-0'>
      <Container className='py-sm'>
        <MaxWidthContainer className='tw-mx-auto' xs={960}>
          <div className='row tw-mb-18 lg:tw-mb-14'>
            <div className='col-lg-6 tw-mb-18'>
              <div className='tw-flex tw-items-end tw-justify-start lg:tw-h-32 tw-mb-14 lg:tw-mb-6'>
                <img
                  alt='an illustration of a Word doc turning into Blissbook policies'
                  src={assets.wordToBlissbook}
                  style={{
                    maxHeight: '149px',
                    maxWidth: '229px',
                  }}
                />
              </div>
              <Typography variant='h2'>Easy to Get Started</Typography>
              <Typography variant='p2'>
                Import one or multiple policies from one or multiple Word docs,
                all at the same time. Collaborate, review, approve, distribute –
                all in one app!
              </Typography>
            </div>

            <div className='col-lg-6 tw-mb-18'>
              <div className='tw-flex tw-items-end tw-justify-start lg:tw-h-32 tw-mb-14 lg:tw-mb-6'>
                <img
                  alt='an illustration depicting multiple versions of content containing redlines'
                  src={assets.versionHistoryIllustration}
                  style={{
                    maxHeight: '139px',
                    maxWidth: '229px',
                  }}
                />
              </div>
              <Typography variant='h2'>Live in the Past, Man</Typography>
              <Typography variant='p2'>
                Feeling nostalgic for policies of yore? Track every edit and
                event. No guesswork; no gaps; you've got a full audit trail you
                can trust.
              </Typography>
            </div>

            <div className='col-lg-6 tw-mb-18'>
              <div className='tw-flex tw-items-end tw-justify-start lg:tw-h-36 tw-mb-14 lg:tw-mb-6'>
                <div
                  className='tw-p-1 box-shadow'
                  style={{
                    borderRadius: 5,
                    display: 'inline-block',
                  }}
                >
                  <img
                    alt='an illustration representing a signature on a dotted line'
                    src={assets.eSignature}
                    style={{
                      maxHeight: '149px',
                      maxWidth: '229px',
                    }}
                  />
                </div>
              </div>
              <Typography variant='h2'>Electronic Signatures</Typography>
              <Typography variant='p2'>
                Request, track, and report on electronic signatures. Automate
                the whole process by integrating with your HRIS, payroll, or
                Active Directory.
              </Typography>
            </div>

            <div className='col-lg-6 tw-mb-18'>
              <div className='tw-flex tw-items-end tw-justify-start lg:tw-h-36 tw-mb-14 lg:tw-mb-6'>
                <div
                  className='tw-p-6 box-shadow'
                  style={{
                    borderRadius: 5,
                    display: 'inline-block',
                  }}
                >
                  <img
                    alt='an illustration of a reporting page depicting employees who have signed, viewed, or have been notified about their policies'
                    src={assets.reporting}
                    style={{
                      maxHeight: '149px',
                      maxWidth: '229px',
                    }}
                  />
                </div>
              </div>
              <Typography variant='h2'>Signature Reporting</Typography>
              <Typography variant='p2'>
                Easy-to-use yet powerful reporting lets you slice and dice your
                employee population, save custom reports, and more.
              </Typography>
            </div>

            <div className='col-lg-6 tw-mb-18'>
              <div
                className='tw-mb-14 lg:tw-mb-6 tw-p-4 box-shadow'
                style={{
                  borderRadius: 5,
                  display: 'inline-block',
                }}
              >
                <img
                  alt='a example of an annotation'
                  src={assets.annotation}
                  style={{
                    maxHeight: '149px',
                    maxWidth: '229px',
                  }}
                />
              </div>
              <Typography variant='h2'>
                Communicate Changes with Employees
              </Typography>
              <Typography variant='p2'>
                Keep thousands of employees updated in a personalized way.
                Attach{' '}
                <Link to='/policy-communication'>
                  digital sticky notes to policies
                </Link>{' '}
                to provide context alongside new or updated content.
              </Typography>
            </div>
            <div className='col-lg-6 lg:tw-mt-0 tw-mt-8'>
              <div
                className='tw-grid tw-grid-cols-3 tw-gap-8 tw-mb-14 lg:tw-mb-6'
                style={{
                  maxWidth: '600px',
                  margin: '0 auto',
                }}
              >
                {[
                  { src: assets.workdayLogo, alt: 'Workday logo' },
                  { src: assets.ukgLogo, alt: 'Ultimate Software logo' },
                  {
                    src: assets.activeDirLogo,
                    alt: 'Microsoft Active Directory logo',
                  },
                  { src: assets.dayforceLogo, alt: 'Dayforce logo' },
                  { src: assets.oktaLogo, alt: 'Okta logo' },
                  { src: assets.samlLogo, alt: 'SAML 2.0 logo' },
                  { src: assets.adpLogo, alt: 'ADP logo' },
                  { src: assets.bambooLogo, alt: 'BambooHR logo' },
                  { src: assets.gWorkspaceLogo, alt: 'Google Workspace logo' },
                ].map((logo, i) => (
                  <div
                    key={i}
                    className='tw-flex tw-items-center tw-justify-center'
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      style={{
                        maxHeight: '30px',
                        maxWidth: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                ))}
              </div>
              <Typography variant='h2'>
                Securely Automate On- and Off-boarding
              </Typography>
              <Typography variant='p2'>
                Automatically add or remove employees to and from Blissbook by
                integrating with HRIS, payroll, &amp; SSO providers like
                Workday, UKG, ADP, Entra, Okta, Paylocity, BambooHR,{' '}
                <a href='/integrations'>and more</a>.
              </Typography>
            </div>
          </div>

          <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-8 tw-mb-24 lg:tw-mb-14'>
            <div className='tw-flex tw-items-center tw-justify-center tw-flex-1'>
              <div
                className='tw-p-4 box-shadow'
                style={{
                  borderRadius: 5,
                  display: 'inline-block',
                }}
              >
                <img
                  alt='an example of limiting access to content'
                  src={assets.chapterVisibility}
                  style={{
                    maxHeight: '140px',
                  }}
                />
              </div>
            </div>
            <div className='tw-flex-1 md:tw-flex-[2] tw-flex tw-flex-col tw-justify-center'>
              <Typography variant='h2'>Personalized Content</Typography>
              <Typography className='tw-mb-0' variant='p2'>
                Dozens of handbooks become one with Blissbook's{' '}
                <Link to='/personalized-handbooks'>personalized content</Link>{' '}
                features. Employees simply sign in and see the content that’s
                relevant to them. You edit in one place.
              </Typography>
            </div>
          </div>

          <div className='tw-flex tw-flex-col md:tw-flex-row-reverse tw-gap-6 tw-mb-24 lg:tw-mb-14'>
            <div className='tw-flex tw-items-center tw-justify-center tw-flex-1'>
              <img
                alt='an illustration of a group of people with the person in front looking authoritative'
                src={assets.manager}
                style={{
                  maxHeight: '126px',
                }}
              />
            </div>
            <div className='tw-flex-1 md:tw-flex-[2] tw-flex tw-flex-col tw-justify-center'>
              <Typography variant='h2'>Empower Managers</Typography>
              <Typography className='tw-mb-0' variant='p2'>
                Blissbook brings managers into the loop with their{' '}
                <Link to='/manager-participation'>own dashboard</Link> to track
                signatures and send reminders. Missed deadlines self-escalate,
                so you can achieve compliance goals without doing all the work.
              </Typography>
            </div>
          </div>

          <div className='tw-flex tw-flex-col md:tw-flex-row tw-gap-8 tw-mb-24 lg:tw-mb-14'>
            <div className='tw-flex tw-items-center tw-justify-center tw-flex-1'>
              <div
                className='tw-p-4 box-shadow'
                style={{
                  borderRadius: 5,
                  display: 'inline-block',
                }}
              >
                <img
                  alt='an example of a law change alert'
                  src={assets.lawChangeAlerts}
                  style={{
                    maxHeight: '140px',
                  }}
                />
              </div>
            </div>
            <div className='tw-flex-1 md:tw-flex-[2] tw-flex tw-flex-col tw-justify-center'>
              <Typography variant='h2'>Law Change Alerts</Typography>
              <Typography variant='p2'>
                Blissbook’s{' '}
                <Link to='/employee-handbook-hr-policy-content'>
                  Law Change Alerts
                </Link>{' '}
                keep you informed of changes in US federal, state, county, and
                city laws that may require you to update your handbook policies.
              </Typography>
            </div>
          </div>
        </MaxWidthContainer>
      </Container>
      <BackgroundShapeGrid
        className='tw-hidden lg:tw-block'
        alignX='left'
        alignY='bottom'
        style={{
          position: 'relative',
          bottom: '-120px',
        }}
        shapes={[
          { x: 0, y: 2, type: 'square', color: 'red-700', fill: true },
          {
            x: 1,
            y: 1,
            type: 'wedge',
            color: 'red-300',
            rotate: 0,
            fill: true,
          },
        ]}
      />
    </Section>

    <Section>
      <Container className='py-sm'>
        <MaxWidthContainer className='tw-mx-auto' xs={960}>
          <div className='tw-flex tw-flex-col lg:tw-flex-row-reverse tw-gap-8'>
            <div className='tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center'>
              <DesktopBrowser className='box-shadow' style={{ width: 400 }}>
                <img
                  alt='a desktop'
                  src='/images/marketing/home/Create-desktop.svg'
                  width='100%'
                />
              </DesktopBrowser>
              <MobileBrowser
                className='box-shadow'
                style={{
                  marginTop: -100,
                  marginRight: -320,
                  width: 150,
                  zIndex: 1,
                }}
              >
                <img
                  alt='a mobile browser'
                  src='/images/marketing/home/Create-mobile.svg'
                  width='100%'
                  style={{
                    background: `linear-gradient(white 50%,${colors['blurple-200']} 50%)`,
                  }}
                />
              </MobileBrowser>
            </div>

            <div className='tw-flex-1'>
              <Typography variant='h2'>100% Custom Branded</Typography>
              <Typography variant='p2'>
                Asking employees to read policies should feel like a reasonable
                request.
              </Typography>
              <Typography variant='p2'>
                Your{' '}
                <Link to='/branded-employee-handbook'>
                  employees’ experience can be 100% custom branded
                </Link>
                , with a custom email sending account, a secure custom domain,
                and a custom look and feel for your handbooks, policies, other
                documents, and even your emails.
              </Typography>
              <Typography variant='p2'>
                Our design services team can help you design and build
                everything and you’ll be able to easily update your documents
                and emails after you launch, on your own, without breaking
                anything.
              </Typography>
              <Typography className='tw-mb-0' variant='p2'>
                No more getting on some other team's calendar to push a change
                out to employees!
              </Typography>
            </div>
          </div>
        </MaxWidthContainer>
      </Container>
    </Section>

    <Section
      style={{
        background: `linear-gradient(180deg, white 0%, ${colors['red-50']} 100%)`,
      }}
    >
      <Container className='py-sm'>
        <MaxWidthContainer className='tw-mx-auto' xs={960}>
          <div className='text-center'>
            <Typography variant='h2'>Why Blissbook?</Typography>
            <Typography
              variant='p1'
              className='tw-mx-auto'
              style={{ maxWidth: '720px' }}
            >
              <Link to='/customers'>Customers love us!</Link> Blissbook is the
              original culture-first employee handbook platform, built to help
              organizations of all sizes manage policies in a people-first
              manner.
            </Typography>
          </div>
          <div className='row'>
            <div className='col-lg-4'>
              <Card
                className='text-left lg:tw-mx-2 tw-mb-10 lg:tw-mb-0'
                color='sunshine-500'
                title='We’ve Got Your Back'
                titleCss={{ textAlign: 'left' }}
                shape=''
              >
                <Typography variant='ul2' style={{ fontSize: 17 }}>
                  <li>Content Migration & Import</li>
                  <li>Content Services</li>
                  <li>Design Services</li>
                  <li>Copywriting/Editing</li>
                  <li>Project Management</li>
                  <li>Unlimited Training</li>
                  <li>Dedicated Account Manager</li>
                  <li>Law Change Alerts</li>
                </Typography>
              </Card>
            </div>
            <div className='col-lg-4'>
              <Card
                className='text-left lg:tw-mx-2 tw-mb-10 lg:tw-mb-0'
                color='blurple-500'
                title='The Features You Need'
                titleCss={{ textAlign: 'left' }}
                shape=''
              >
                <Typography variant='ul2' style={{ fontSize: 17 }}>
                  <li>Policy Management</li>
                  <li>Review + Approval Workflows</li>
                  <li>eSignatures &amp; Reporting</li>
                  <li>Automated Reminders</li>
                  <li>Change Annotations</li>
                  <li>Personalized Content</li>
                  <li>Manager Escalation</li>
                  <li>Foreign Languages</li>
                  <li>Version History</li>
                  <li>Mobile-Friendly</li>
                  <li>Customized Notifications</li>
                  <li>Role-Based Admin Access</li>
                  <li>No-Password Sign-In</li>
                </Typography>
              </Card>
            </div>
            <div className='col-lg-4'>
              <Card
                className='text-left lg:tw-mx-2 tw-mb-10 lg:tw-mb-0'
                color='aqua-500'
                title='Secure &amp; Redundant'
                titleCss={{ textAlign: 'left' }}
                shape=''
              >
                <Typography variant='ul2' style={{ fontSize: 17 }}>
                  <li>Automated Employee Sync</li>
                  <li>HRIS &amp; SSO Integrations</li>
                  <li>Secure Custom Domain</li>
                  <li>Custom Email Sender</li>
                  <li>
                    Enterprise Level{' '}
                    <Link to='/information-security'>Security</Link>
                  </li>
                  <li>GDPR &amp; CCPA Compliant</li>
                  <li>SOC 2 Type II</li>
                  <li>99.9% Guaranteed Uptime</li>
                </Typography>
              </Card>
            </div>
          </div>
        </MaxWidthContainer>
      </Container>
    </Section>

    <CallToActionSection
      title='Ready to digitize your company policies?'
      description='Hundreds of companies, large and small, trust Blissbook with their policy management. Tell us about your project and we’ll give your team a customized demo.'
      contentClassName=''
      includeLogos
    >
      <div className='md:tw-flex tw-items-center tw-mb-8'>
        <Link className='btn btn-primary tw-mr-6' to='/request-a-demo'>
          Request a Demo
        </Link>
        <ForwardLink className='tw-block tw-my-6' to='/pricing'>
          Learn more
        </ForwardLink>
      </div>
    </CallToActionSection>
  </MarketingLayout>
)
